import { useEffect, useState } from "react";
import { clientLog } from "../clientLog";
import { APP_BUILD, onServerBreak, WEB_BREAK } from "../release";
import { reloadOnce } from "./chunk";
import { edited, holds, markTap, markTyped, onHoldChange, saveDrafts, setReloading, setShownPath } from "./hold";
import { waitingOnPhone } from "./outbox";
import { CURRENT, type Effect, JITTER_MAX_MS, type PolicyEvent, type PolicyState, step, type Trigger } from "./reloadPolicy";
import { CHECK_MS, checkedText, fetchVersion, isNewBuild, TICK_MS, type VersionFile, woke } from "./version";
import { whenWritesDone } from "./writes";

export { APP_BUILD };

type Listener = () => void;
type Log = (message: string, detail: Record<string, unknown>) => void;

export const GUARD_KEY = "xflow-reload-guard";
export const GUARD_MS = 3_600_000;
export const GUARD_TRIES = 2;
export const WRITES_MAX_MS = 30_000;
export const HOOK_MAX_MS = 5_000;
export const LOAD_WINDOW_MS = 10_000;
export const CHECK_GAP_MS = 60_000;
export const ACTIVATE_MAX_MS = 3_000;

interface Guard {
  from: string;
  target: string;
  attempts: number;
  at: number;
}

interface Deps {
  unsent: () => Promise<number>;
  container: () => Pick<ServiceWorkerContainer, "addEventListener" | "controller"> | undefined;
  reload: () => void;
  storage: () => Pick<Storage, "getItem" | "setItem" | "removeItem"> | undefined;
  now: () => number;
  random: () => number;
  version: () => Promise<VersionFile | null>;
  log: Log;
}

const DEFAULTS: Deps = {
  unsent: waitingOnPhone,
  container: () => navigator.serviceWorker,
  reload: () => location.reload(),
  storage: () => sessionStorage,
  now: Date.now,
  random: Math.random,
  version: () => fetchVersion(),
  log: (message, detail) => console.info(`[update] ${message}`, detail),
};
let deps: Deps = DEFAULTS;

let policy: PolicyState = CURRENT;
let remote: VersionFile | null = null;
let checkedAt: number | null = null;
let lastCheckAsk = 0;
let shell: string | null = null;
let waiting: ServiceWorker | null = null;
let registration: Pick<ServiceWorkerRegistration, "update"> | null = null;
let applying = false;
let adopting = false;
let hiddenSince: number | null = null;
let lastActive = 0;
let startedAt = 0;
let lastDeferred = "";
let updatedFrom: string | null = null;
let chain: Promise<unknown> = Promise.resolve();
let timer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<Listener>();
const beforeReload = new Set<() => Promise<void> | void>();

const tell = () => listeners.forEach((listener) => listener());

/** Run a function before a forced reload, for example to write a cache to disk. */
export function onBeforeReload(hook: () => Promise<void> | void): () => void {
  beforeReload.add(hook);
  return () => beforeReload.delete(hook);
}

export function onUpdate(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function readGuard(): Guard | null {
  try {
    const kept = deps.storage()?.getItem(GUARD_KEY);
    return kept ? (JSON.parse(kept) as Guard) : null;
  } catch {
    return null;
  }
}

function writeGuard(guard: Guard | null): void {
  try {
    if (guard) deps.storage()?.setItem(GUARD_KEY, JSON.stringify(guard));
    else deps.storage()?.removeItem(GUARD_KEY);
  } catch {
    // Without session storage, the guard is lost; the policy still waits for a safe moment.
  }
}

/** True after GUARD_TRIES reloads that did not change the build, for GUARD_MS. */
export function guarded(now = deps.now()): boolean {
  const guard = readGuard();
  return guard !== null && guard.from === APP_BUILD && guard.attempts >= GUARD_TRIES && now - guard.at < GUARD_MS;
}

/** At boot: a guard of a different build means that the reload worked. */
function readGuardAtBoot(): void {
  const guard = readGuard();
  if (!guard) return;
  const now = deps.now();
  if (guard.from !== APP_BUILD) {
    updatedFrom = guard.from;
    writeGuard(null);
    clientLog.event("updated", { from: guard.from, to: APP_BUILD, attempts: guard.attempts });
    deps.log(`updated from ${guard.from} to ${APP_BUILD}`, { attempts: guard.attempts });
    return;
  }
  if (now - guard.at >= GUARD_MS) return writeGuard(null);
  if (guard.attempts >= GUARD_TRIES) {
    clientLog.event("loop_guard", { target: guard.target, attempts: guard.attempts, wait_min: Math.ceil((GUARD_MS - (now - guard.at)) / 60_000) });
    console.warn(`[update] ${guard.attempts} reloads did not load build ${guard.target}; no auto reload for 1 h`);
  }
}

/** The version that a worker reports; an old worker gives no answer. */
export function askVersion(worker: Pick<ServiceWorker, "postMessage"> | null | undefined, waitMs = 1_000): Promise<string> {
  if (!worker || typeof MessageChannel === "undefined") return Promise.resolve("unknown");
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const done = (version: string) => {
      clearTimeout(timer);
      channel.port1.close();
      resolve(version);
    };
    const timer = setTimeout(() => done("unknown"), waitMs);
    channel.port1.onmessage = (event) => done(String(event.data));
    try {
      worker.postMessage({ type: "VERSION" }, [channel.port2]);
    } catch {
      done("unknown");
    }
  });
}

const atLoad = () => deps.now() - startedAt < LOAD_WINDOW_MS && lastActive <= startedAt;

interface Leaving {
  holds: string[];
  edited: boolean;
}

/** One step of the policy, one at a time. `leaving`: the state of the page that the user leaves. */
function run(event: PolicyEvent, leaving?: Leaving): Promise<Effect[]> {
  const next = chain.then(async () => {
    const unsent = await deps.unsent().catch(() => 0);
    const now = deps.now();
    const page = leaving ?? { holds: holds(), edited: edited() };
    const result = step(policy, event, { now, hiddenSince, lastActive, ...page, unsent, guarded: guarded(now) });
    policy = result.state;
    for (const effect of result.effects) handle(effect);
    schedule();
    tell();
    return result.effects;
  });
  chain = next.catch(() => undefined);
  return next;
}

function handle(effect: Effect): void {
  const detail = { target: policy.target, build: APP_BUILD };
  switch (effect.kind) {
    case "pending":
      clientLog.event("new_build", { target: effect.target, night_at: new Date(effect.nightAt).toISOString(), break: remote?.break ?? null });
      return deps.log(`new build ${effect.target}; the page loads it at a safe moment`, detail);
    case "due":
      clientLog.event("due", { target: policy.target, why: effect.why });
      return deps.log(`the new build is due (${effect.why})`, detail);
    case "countdown":
      clientLog.event("countdown", { seconds: effect.ms / 1000, snooze: effect.snooze });
      return deps.log(`reload in ${effect.ms / 1000} s`, detail);
    case "snooze":
      clientLog.event("snooze", { count: effect.count });
      return deps.log(`reload waits 30 min (snooze ${effect.count})`, detail);
    case "deferred": {
      const key = `${effect.at}:${effect.reasons.join(",")}`;
      if (key === lastDeferred) return;
      lastDeferred = key;
      clientLog.event("deferred", { at: effect.at, reasons: effect.reasons.join(",") });
      return deps.log(`reload deferred (${effect.at}): ${effect.reasons.join(", ")}`, detail);
    }
    case "held_long":
      clientLog.event("held_long", { hours: effect.hours, labels: effect.labels.join(",") });
      return console.warn(`[update] a form held the new build for ${effect.hours} h: ${effect.labels.join(", ")}`);
    case "reload":
      void performReload(effect.reason);
  }
}

/** A timer for the end of the countdown or the snooze, so that the reload does not wait for the next tick. */
function schedule(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  const at = policy.phase === "due" ? (policy.countdownEnd ?? policy.snoozedUntil) : null;
  if (at === null) return;
  timer = setTimeout(() => void run({ type: "check", trigger: "tick" }), Math.max(0, at - deps.now()) + 50);
}

async function withLimit(work: Promise<void> | void, ms: number): Promise<boolean> {
  return Promise.race([Promise.resolve(work).then(() => true), new Promise<boolean>((resolve) => setTimeout(() => resolve(false), ms))]).catch(() => false);
}

/** Wait for the writes, run the hooks, save the drafts, then load the new build. */
export async function performReload(reason: string): Promise<void> {
  setReloading(true);
  const started = deps.now();
  const writesDone = await whenWritesDone(WRITES_MAX_MS);
  let hooksDone = 0;
  for (const hook of beforeReload) if (await withLimit(hook(), HOOK_MAX_MS)) hooksDone += 1;
  const drafts = await saveDrafts(deps.log);
  const before = readGuard();
  const target = policy.target ?? "unknown";
  const attempts = before && before.from === APP_BUILD && started - before.at < GUARD_MS ? before.attempts + 1 : 1;
  writeGuard({ from: APP_BUILD, target, attempts, at: started });
  const detail = { reason, from: APP_BUILD, to: target, attempts, writes_done: writesDone, hooks: hooksDone, drafts, wait_ms: deps.now() - started };
  clientLog.event("reload", detail);
  deps.log(`reload (${reason}) from ${APP_BUILD} to ${target}`, detail);
  activate();
}

/** The waiting worker takes control, then the page loads again; with no waiting worker, a plain reload. */
function activate(): void {
  applying = true;
  const container = deps.container();
  if (!waiting || !container) return deps.reload();
  let done = false;
  const go = () => {
    if (done) return;
    done = true;
    deps.reload();
  };
  container.addEventListener("controllerchange", go, { once: true } as AddEventListenerOptions);
  setTimeout(go, ACTIVATE_MAX_MS);
  waiting.postMessage({ type: "SKIP_WAITING" });
}

/** A page that is the new build takes the waiting worker with no reload, so that the next load uses it. */
function adopt(worker: ServiceWorker, why: string): void {
  adopting = true;
  deps.log(`the page is build ${APP_BUILD} already; the new worker takes control with no reload (${why})`, {});
  worker.postMessage({ type: "SKIP_WAITING" });
}

/** Read the version file. A different build moves the policy to PENDING; the same build sets it back to CURRENT. */
export async function checkVersion(reason: string, force = false): Promise<VersionFile | null> {
  const now = deps.now();
  if (!force && now - lastCheckAsk < CHECK_GAP_MS) return remote;
  lastCheckAsk = now;
  const file = await deps.version();
  if (file) {
    checkedAt = deps.now();
    remote = file;
  }
  const result = file === null ? "none" : isNewBuild(file, APP_BUILD) ? "new" : "same";
  if (result !== "same" || reason === "load" || reason === "wake") clientLog.event("check", { reason, result, remote: file?.build ?? null, break: file?.break ?? null });
  if (isNewBuild(file, APP_BUILD)) {
    void registration?.update().catch(() => undefined);
    await newBuild(file.build, file.break);
  } else if (file && policy.phase !== "current" && policy.phase !== "reloading") {
    deps.log(`the version file names this build again; the update stops`, { build: APP_BUILD });
    await run({ type: "current" });
  }
  tell();
  return file;
}

async function newBuild(target: string, serverBreak: number): Promise<void> {
  await run({ type: "new_build", target, serverBreak, ownBreak: WEB_BREAK, jitterMs: Math.floor(deps.random() * JITTER_MAX_MS) });
  if (atLoad()) await run({ type: "check", trigger: "load" });
}

/** A new worker is waiting: the page loads it, unless the version file says that the page is the new build. */
async function workerFound(worker: ServiceWorker, controlled: boolean): Promise<void> {
  waiting = worker;
  const version = await askVersion(worker);
  if (!controlled || (remote && remote.build === APP_BUILD)) return adopt(worker, controlled ? "version file" : "no controller");
  deps.log(`a new worker ${version} waits`, { build: APP_BUILD });
  await newBuild(remote?.build && remote.build !== APP_BUILD ? remote.build : `shell ${version}`, remote?.break ?? 0);
}

/** Watch for a new worker that installs while the page is open. */
export function watchRegistration(found: ServiceWorkerRegistration, controlled: () => boolean): Promise<void> {
  registration = found;
  found.addEventListener("updatefound", () => {
    const worker = found.installing;
    worker?.addEventListener("statechange", () => {
      if (worker.state === "installed") void workerFound(worker, controlled());
    });
  });
  return found.waiting ? workerFound(found.waiting, controlled()) : Promise.resolve();
}

/** The controller changed: our own reload, our own adopt, or a different tab that loaded a new build. */
export async function controllerChanged(): Promise<void> {
  shell = await askVersion(deps.container()?.controller);
  if (applying) return;
  if (adopting) {
    adopting = false;
    tell();
    return;
  }
  const file = await checkVersion("controllerchange", true);
  if (file === null) await newBuild(`shell ${shell}`, 0);
}

/** An in-app navigation: the holds are those of the page that the user leaves. */
export function navigated(path: string): Promise<Effect[]> {
  const leaving = { holds: holds(), edited: edited() };
  setShownPath(path);
  return run({ type: "check", trigger: "navigate" }, leaving);
}

export function check(trigger: Trigger): Promise<Effect[]> {
  return run({ type: "check", trigger });
}

/** The "Update now" button. */
export function updateNow(): Promise<Effect[]> {
  return run({ type: "update_now" });
}

/** The "Wait 30 min" button. */
export function snooze(): Promise<Effect[]> {
  return run({ type: "snooze" });
}

function visibility(): void {
  if (document.visibilityState === "hidden") {
    hiddenSince = deps.now();
    return;
  }
  void run({ type: "check", trigger: "visible" }).finally(() => {
    hiddenSince = null;
    void checkVersion("visible");
  });
}

let lastTick = 0;
function tick(): void {
  const now = deps.now();
  const slept = woke(lastTick, now);
  lastTick = now;
  if (slept) void checkVersion("wake", true);
  else if (document.visibilityState === "visible" && (checkedAt === null || now - checkedAt >= CHECK_MS)) void checkVersion("poll");
  void run({ type: "check", trigger: "tick" });
}

/** Start the version checks, the policy and the worker. */
export function startUpdates(): void {
  startedAt = deps.now();
  lastTick = startedAt;
  lastActive = startedAt;
  document.documentElement.dataset.build = APP_BUILD;
  console.info("[update] page build", { build: APP_BUILD, break: WEB_BREAK });
  readGuardAtBoot();
  clientLog.start();
  setShownPath(location.pathname);
  const active = () => (lastActive = deps.now());
  for (const name of ["keydown", "pointerdown", "touchstart", "wheel"]) addEventListener(name, active, { capture: true, passive: true });
  document.addEventListener("input", (event) => markTyped(event.target), true);
  document.addEventListener("change", (event) => markTyped(event.target), true);
  document.addEventListener("click", (event) => markTap(event.target), true);
  addEventListener("popstate", () => void navigated(location.pathname));
  document.addEventListener("visibilitychange", visibility);
  for (const name of ["focus", "online", "pageshow"]) addEventListener(name, () => void checkVersion(name));
  addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    clientLog.event("crash", { kind: "preload" });
    if (!reloadOnce()) console.warn("[update] a file of the page did not load, and the page reloaded a short time ago");
  });
  onServerBreak((value) => {
    deps.log(`the API asks for web break ${value}; this page has ${WEB_BREAK}`, {});
    void checkVersion("break_header", true);
  });
  onHoldChange(() => {
    if (policy.phase === "due") void run({ type: "check", trigger: "tick" });
  });
  setInterval(tick, TICK_MS);
  void checkVersion("load", true).then(async (file) => {
    if ("serviceWorker" in navigator) await registerWorker();
    clientLog.event("version_seen", { remote: file?.build ?? null, shell, break: WEB_BREAK, standalone: matchMedia?.("(display-mode: standalone)").matches ?? false });
  });
}

async function registerWorker(): Promise<void> {
  const container = navigator.serviceWorker;
  const controlled = () => container.controller !== null;
  container.addEventListener("controllerchange", () => void controllerChanged());
  shell = await askVersion(container.controller);
  tell();
  try {
    const found = await container.register("/sw.js");
    await watchRegistration(found, controlled);
  } catch (error) {
    console.warn("[update] no service worker; the app works online only", error);
  }
}

export interface UpdateView {
  phase: PolicyState["phase"];
  target: string | null;
  countdownEnd: number | null;
  canSnooze: boolean;
  snoozesLeft: number;
  holds: string[];
  updatedFrom: string | null;
}

export function updateView(): UpdateView {
  return {
    phase: policy.phase,
    target: policy.target,
    countdownEnd: policy.countdownEnd,
    canSnooze: policy.canSnooze,
    snoozesLeft: Math.max(0, 2 - policy.snoozes),
    holds: policy.phase === "current" ? [] : holds(),
    updatedFrom,
  };
}

export function dismissUpdated(): void {
  updatedFrom = null;
  tell();
}

export function useUpdateView(): UpdateView {
  const [view, setView] = useState(updateView);
  useEffect(() => {
    const stopUpdate = onUpdate(() => setView(updateView()));
    const stopHold = onHoldChange(() => setView(updateView()));
    return () => {
      stopUpdate();
      stopHold();
    };
  }, []);
  return view;
}

/** The build time (UTC "YYYY-MM-DD HH:MM") in the time of the device, with its zone; other builds as they are. */
export function buildLabel(build: string = APP_BUILD, timeZone?: string): string {
  const at = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(build) ? new Date(`${build.replace(" ", "T")}:00Z`) : null;
  if (!at || Number.isNaN(at.getTime())) return build;
  return at.toLocaleString(undefined, { timeZone, year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" });
}

/** The text of the version of the page and of the last check. */
export function versionText(now = deps.now(), timeZone?: string): string {
  return [`Version ${buildLabel(APP_BUILD, timeZone)}`, checkedText(checkedAt, now)].join(" · ");
}

/** The hover text of the version line: the build in UTC and the shell. */
export function versionTitle(): string {
  const parts = [`Build ${APP_BUILD} UTC`];
  if (shell && shell !== "unknown") parts.push(`shell ${shell}`);
  return parts.join(" · ");
}

export function useVersionText(): string {
  const [text, setText] = useState(() => versionText());
  useEffect(() => {
    const stop = onUpdate(() => setText(versionText()));
    const clock = setInterval(() => setText(versionText()), 30_000);
    return () => {
      stop();
      clearInterval(clock);
    };
  }, []);
  return text;
}

/** For tests. */
export function resetUpdate(next: Partial<Deps> = {}): void {
  deps = { ...DEFAULTS, ...next };
  policy = CURRENT;
  remote = null;
  checkedAt = null;
  lastCheckAsk = 0;
  shell = null;
  waiting = null;
  registration = null;
  applying = false;
  adopting = false;
  hiddenSince = null;
  startedAt = deps.now();
  lastActive = startedAt;
  lastDeferred = "";
  updatedFrom = null;
  chain = Promise.resolve();
  if (timer) clearTimeout(timer);
  timer = null;
  listeners.clear();
  beforeReload.clear();
}

/** For tests: the state of the policy, and the boot check of the loop guard. */
export const forTests = {
  policy: () => policy,
  bootGuard: readGuardAtBoot,
  setActive: (at: number) => (lastActive = at),
  setHidden: (at: number | null) => (hiddenSince = at),
};
